import cx from "classnames";
import debounce from "lodash/debounce";
import get from "lodash/get";
import isNull from "lodash/isNull";
import upperFirst from "lodash/upperFirst";
import { memo, useCallback, useEffect } from "react";
import { FormProvider } from "react-hook-form";
import { checkmarkCircle, closeCircle } from "ionicons/icons";
import { IonIcon, IonLabel, useIonViewDidEnter } from "@ionic/react";

import s from "./WLPromoField.module.scss";
import useWLPromoStore from "./store";
import { useEffectOnlyOnce } from "../../../Common/utils/common";
import { useWLPromoFieldProvider } from "./provider";
import { iWLPromoField } from "../../../Common/models/promos";
import { WLFormInput } from "../../../Common/components/WLForms/WLFormInput/WLFormInput";
import {
  getStripePromoLabel,
  getWLPromoDescription,
} from "../../services/promo";
import { isEmpty } from "lodash";

const WLPromoField = ({
  selectedSubscription,
  forPowerUp,
  forSignUp,
  forUpgrade,
  forBillingIssue,
  forPowerUpUsingPoints,
  hidden,
}: iWLPromoField) => {
  const { promoField, useFormMethods } = useWLPromoFieldProvider(
    selectedSubscription || null,
    {
      forPowerUp,
      forPowerUpUsingPoints,
      forSignUp,
      forUpgrade,
      forBillingIssue,
    },
    forPowerUpUsingPoints
  );

  const {
    setValue,
    trigger,
    resetField,
    formState: { errors },
    clearErrors,
  } = useFormMethods;

  const {
    validWLPromo,
    validStripeCoupon,
    validPromoCode,
    setPromoField,
    setPromoFieldValueFunc,
    setTriggerFieldValidationFunc,
    setResetPromoFieldFunc,
    reset,
  } = useWLPromoStore();

  const isValidWLPromo = validWLPromo !== undefined && !isNull(validWLPromo);
  const isValidStripeCoupon =
    validStripeCoupon !== undefined && !isNull(validStripeCoupon);
  const isValidPromo = isValidWLPromo || isValidStripeCoupon;

  const resetPromoField = useCallback(
    debounce(() => {
      resetField(promoField.name);
      reset();
    }, 250),
    []
  );

  //to watch for loose errors
  useEffect(() => {
    const errorMsg = get(errors, `${promoField.name}.message`, undefined) as
      | string
      | undefined;

    const hasErrors = !!errors && errorMsg !== undefined;
    if ((validWLPromo || validStripeCoupon) && hasErrors) {
      clearErrors();
    }
  }, [validWLPromo, validStripeCoupon, errors]);

  useEffectOnlyOnce(() => {
    setPromoField(promoField);
    setPromoFieldValueFunc(setValue);
    setTriggerFieldValidationFunc(trigger);
    setResetPromoFieldFunc(resetPromoField);

    if (!isEmpty(validPromoCode)) {
      setValue(promoField.name, validPromoCode);
    }
  });

  const renderPromoInputIcon = () => {
    if (isValidPromo) {
      return (
        <IonIcon
          slot="end"
          icon={checkmarkCircle}
          aria-hidden="true"
          color="success"
          size="large"
        />
      );
    } else {
      return (
        <IonIcon
          style={{
            display: !isValidPromo ? "none" : "",
          }} // hide to prevent issue in removing a child
          slot="end"
          icon={closeCircle}
          aria-hidden="true"
          color="danger"
          size="large"
        />
      );
    }
  };

  const wlPromoDescription =
    selectedSubscription !== undefined &&
    !isNull(selectedSubscription) &&
    upperFirst(
      `${isValidStripeCoupon
          ? getStripePromoLabel(
            validStripeCoupon,
            forPowerUp,
            selectedSubscription.price
          )
          : ""
        }
  ${isValidStripeCoupon && isValidWLPromo ? " & " : ""}${isValidWLPromo
          ? getWLPromoDescription(
              validWLPromo,
              selectedSubscription,
              forPowerUp,
              !!forPowerUpUsingPoints
            )
          : ""
        }`.trim()
    );
  return (
    <FormProvider {...useFormMethods}>
      <form
        className={cx(s.promoCodeForm, "ion-margin-top")}
        onSubmit={(event) => {
          event.preventDefault();
        }}
      >
        <WLFormInput {...promoField} hidden={!!hidden}>
          {renderPromoInputIcon()}
        </WLFormInput>
        {isValidPromo &&
          wlPromoDescription &&
          (hidden ? !forBillingIssue : true) && (
            <div
              className={cx(s.promoDescriptionContainer, "ion-margin-bottom")}
            >
              <IonLabel
                className={cx(s.subscriptionCoupon, "wl-body-2-gilroy")}
              >
                Promo active:&nbsp;
                {wlPromoDescription}
              </IonLabel>
            </div>
          )}
      </form>
    </FormProvider>
  );
};

export default memo(WLPromoField);
